import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { gridSizeMdLg6, ROUTER_PATHS, useCheckPermissionsArray, useLoaders } from '@/shared';
import {
  CreateCityParams,
  useCreateCity,
  useFetchAllCountries,
  useFetchProvinces,
  useUpdateCity,
} from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  CustomNumberTextField,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import {
  City,
  Country,
  Province,
} from '@/shared/interfaces/erp/system-settings';
import { newCitySchema } from '@/shared/utils';
import { InputAdornment } from '@mui/material';

export type SaveCityProps = {
  editCity?: City;
  titlePage: string;
};

export type SaveFormData = CreateCityParams & {};

const returnUrlCitiesPage = ROUTER_PATHS.administration.ciudadesNav;

const SaveCity: React.FC<SaveCityProps> = ({ titlePage, editCity }) => {
  useCheckPermissionsArray([
    PermissionsEnum.api_view_pais,
    PermissionsEnum.api_view_provincia,
  ]);

  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);

  ///* mutations
  const createCityMutation = useCreateCity({
    navigate,
    returnUrl: returnUrlCitiesPage,
  });
  const updateCityMutation = useUpdateCity({
    navigate,
    returnUrl: returnUrlCitiesPage,
  });

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newCitySchema) as any,
    defaultValues: {
      estado: true,
    },
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = form;

  const watchedCountry = form.watch('pais');

  ///* fetch data
  const {
    data: countriesPaging,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 600,
  });

  const {
    data: provincesPaging,
    isLoading: isLoadingProvinces,
    isRefetching: isRefetchingProvinces,
  } = useFetchProvinces(
    {
      page_size: 600,
      pais: watchedCountry,
    },
    !!watchedCountry
  );

  ///* handlers
  const onSave = () => {
    if (!isValid) return;

    ///* edit
    if (editCity?.id_ciudad) {
      return updateCityMutation.mutate({
        id: editCity.id_ciudad,
        data: {
          ...form.getValues(),
          codigo: form.getValues('codigo') || '',
        },
      });
    }

    ///* create
    return createCityMutation.mutate({
      ...form.getValues(),
    });
  };

  ///* effects
  useEffect(() => {
    if (!editCity) return;
    setShrink(true);
    reset(editCity);
  }, []);
  useLoaders(
    isLoadingProvinces ||
    isLoadingCountries ||
    isRefetchingProvinces ||
    isRefetchingCountries
  );

  if (editCity && (isLoadingCountries || isLoadingProvinces)) return null;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlCitiesPage);
      }}
      onSave={handleSubmit(onSave)}
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message ?? ''}
        shrink={shrink}
      />

      {/* <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        defaultValue={form.getValues().codigo || ''}
        error={errors.codigo}
        helperText={errors.codigo?.message ?? ''}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={!!editCity}
      /> */}

      {/* =========== Autocomplete provinces =========== */}
      <CustomAutocomplete<Country>
        label="País"
        name="pais"
        // options
        options={countriesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().pais || ''}
        optionLabelForEdit={form.getValues().pais || ''}
        isLoadingData={isLoadingCountries}
        // validation
        control={control}
        error={errors.pais}
        helperText={errors.pais?.message}
        size={gridSizeMdLg6}
      />
      <CustomAutocomplete<Province>
        label="Provincia"
        name="provincia"
        // options
        options={provincesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().provincia || ''}
        optionLabelForEdit={form.getValues().provincia || ''}
        isLoadingData={isLoadingProvinces}
        // validation
        control={control}
        error={errors.provincia}
        helperText={errors.provincia?.message}
        size={gridSizeMdLg6}
      />

      <CustomNumberTextField
        label="Metraje autorizado fibra"
        name="metraje_autorizado"
        control={form.control}
        defaultValue={form.getValues().metraje_autorizado || ''}
        error={errors.metraje_autorizado}
        helperText={errors.metraje_autorizado?.message ?? ''}
        shrink={shrink}
        size={gridSizeMdLg6}
        endAdornment={<InputAdornment position="start">m</InputAdornment>}
        min={0}
      />

      <SampleCheckbox
        label="Tiene cobertura"
        name="cobertura"
        control={form.control}
        defaultValue={!!form.getValues().cobertura}
        size={gridSizeMdLg6}
      />
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
        size={gridSizeMdLg6}
      />
    </SingleFormBoxScene>
  );
};

export default SaveCity;
